/**
 * Login Page
 * JWT Authentication with backend API
 * Uses system theme preference (independent of app settings)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, LogIn, Eye, EyeOff, Shield } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { authApi } from '../lib/api';

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuthStore();
  const { applyTheme } = useThemeStore();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Force system theme on login page
  useEffect(() => {
    const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    
    if (systemDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Restore user's theme preference when leaving login page
    return () => {
      applyTheme();
    };
  }, [applyTheme]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);
      const { user, accessToken } = response.data.data;
      // Note: refreshToken is now stored in httpOnly cookie by the server
      
      login(user, accessToken);
      navigate('/');
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { error?: string } } };
      setError(axiosError.response?.data?.error || 'Authentication error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center p-4 relative overflow-hidden transition-colors">
      {/* Background grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.1)_1px,transparent_1px)] dark:bg-[linear-gradient(rgba(51,65,85,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(51,65,85,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />
      
      {/* Glow effects */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 dark:bg-emerald-500/5 rounded-full blur-3xl" />
      <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 dark:bg-blue-500/5 rounded-full blur-3xl" />
      
      <div className="relative z-10 w-full max-w-md">
        {/* Main card */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm border border-slate-200 dark:border-slate-700/50 rounded-xl shadow-2xl p-8">
          {/* Logo */}
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 border border-emerald-500/30 rounded-xl flex items-center justify-center mb-4 shadow-[0_0_20px_rgba(16,185,129,0.2)]">
              <Zap className="w-8 h-8 text-emerald-500 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl font-mono font-bold text-slate-800 dark:text-slate-100 tracking-wide">GRIDINSPECT</h1>
            <p className="text-xs font-mono text-slate-500 tracking-widest mt-1">POWER LINE INSPECTION SYSTEM</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-3 bg-red-500/10 border border-red-500/30 text-red-500 dark:text-red-400 rounded-lg text-sm font-mono">
              <span className="text-red-600 dark:text-red-500 mr-2">[ERROR]</span>{error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-xs font-mono font-medium text-slate-500 dark:text-slate-400 mb-2 tracking-wider">
                EMAIL
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600/50 rounded-lg text-slate-800 dark:text-slate-200 font-mono text-sm placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                placeholder="operator@company.com"
                required
                autoComplete="email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-mono font-medium text-slate-500 dark:text-slate-400 mb-2 tracking-wider">
                PASSWORD
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 bg-slate-50 dark:bg-slate-900/50 border border-slate-300 dark:border-slate-600/50 rounded-lg text-slate-800 dark:text-slate-200 font-mono text-sm placeholder-slate-400 dark:placeholder-slate-600 focus:outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-emerald-600/20 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 py-3 px-4 rounded-lg font-mono font-medium tracking-wider hover:bg-emerald-600/30 hover:border-emerald-500/50 hover:shadow-[0_0_15px_rgba(16,185,129,0.2)] focus:outline-none focus:ring-2 focus:ring-emerald-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-emerald-500/30 dark:border-emerald-400/30 border-t-emerald-500 dark:border-t-emerald-400 rounded-full animate-spin" />
                  <span>AUTHENTICATING...</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>SIGN IN</span>
                </>
              )}
            </button>
          </form>

          {/* Security badge */}
          <div className="mt-6 flex items-center justify-center gap-2 text-slate-400 dark:text-slate-600">
            <Shield className="w-4 h-4" />
            <span className="text-xs font-mono tracking-wider">SECURE CONNECTION</span>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs font-mono text-slate-500 dark:text-slate-600 tracking-wider">
          ELIFLY &copy; 2026 // GRIDINSPECT PORTAL v1.0
        </p>
      </div>
    </div>
  );
}
